import type { CSSProperties, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  EmailRounded,
  Facebook,
  Instagram,
  LanguageRounded,
  PhoneRounded,
  PlaceRounded,
  WhatsApp,
  YouTube,
} from '@mui/icons-material';
import type { LandingContact } from '@dosfilos/domain';
import { safeExternalUrl } from '../../../lib/featured-events';
import { normalizeContact, whatsappHref } from '../../../lib/landing-content';
import { LANDING } from '../landing-theme';

/**
 * El contacto del pie del landing: correo, teléfono, WhatsApp, dirección (con
 * mapa) y redes. Todo lo carga el seminario; lo que no cargó no aparece. Los
 * enlaces se vuelven a validar aquí (solo https) aunque ya se validaron al
 * guardar: lo que se pinta en una página pública no confía en lo guardado.
 */
export function ContactBlock({
  email,
  contact,
}: {
  email?: string | undefined;
  contact?: LandingContact | null | undefined;
}) {
  const { t } = useTranslation('sis');
  const { SANS, TEXT_PRIMARY, TEXT_SECONDARY, TEXT_MUTED, BORDER } = LANDING;
  const c = normalizeContact(contact) ?? {};
  const mapsUrl = safeExternalUrl(c.mapsUrl);
  const place = [c.address, c.city].filter(Boolean).join(', ');

  const linkStyle: CSSProperties = {
    fontFamily: SANS,
    fontSize: 14,
    color: TEXT_SECONDARY,
    textDecoration: 'none',
  };
  const hover = {
    onMouseEnter: (e: { currentTarget: HTMLElement }) =>
      (e.currentTarget.style.color = TEXT_PRIMARY),
    onMouseLeave: (e: { currentTarget: HTMLElement }) =>
      (e.currentTarget.style.color = TEXT_SECONDARY),
  };
  const external = { target: '_blank', rel: 'noopener noreferrer' } as const;
  const row = (icon: ReactNode, body: ReactNode) => (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
      <span style={{ display: 'inline-flex', marginTop: 2, color: TEXT_MUTED }}>{icon}</span>
      {body}
    </div>
  );
  const iconStyle = { fontSize: 14 };

  const socials = (
    [
      ['instagram', c.instagram, <Instagram key="i" style={{ fontSize: 18 }} />],
      ['facebook', c.facebook, <Facebook key="f" style={{ fontSize: 18 }} />],
      ['youtube', c.youtube, <YouTube key="y" style={{ fontSize: 18 }} />],
      ['website', c.website, <LanguageRounded key="w" style={{ fontSize: 18 }} />],
    ] as const
  )
    .map(([key, url, icon]) => ({ key, url: safeExternalUrl(url), icon }))
    .filter((s) => s.url);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {email &&
        row(
          <EmailRounded style={iconStyle} />,
          <a href={`mailto:${email}`} style={linkStyle} {...hover}>
            {email}
          </a>,
        )}
      {c.phone &&
        row(
          <PhoneRounded style={iconStyle} />,
          <a
            href={`tel:${c.phone.replace(/[^\d+]/g, '')}`}
            style={linkStyle}
            aria-label={`${t('publicLanding.footer.phone')}: ${c.phone}`}
            {...hover}
          >
            {c.phone}
          </a>,
        )}
      {c.whatsapp &&
        row(
          <WhatsApp style={iconStyle} />,
          <a href={whatsappHref(c.whatsapp)} style={linkStyle} {...external} {...hover}>
            {t('publicLanding.footer.whatsapp')} +{c.whatsapp}
          </a>,
        )}
      {place &&
        row(
          <PlaceRounded style={iconStyle} />,
          mapsUrl ? (
            <a
              href={mapsUrl}
              style={linkStyle}
              title={t('publicLanding.footer.map')}
              {...external}
              {...hover}
            >
              {place}
            </a>
          ) : (
            <span style={{ ...linkStyle, lineHeight: 1.5 }}>{place}</span>
          ),
        )}
      {!place &&
        mapsUrl &&
        row(
          <PlaceRounded style={iconStyle} />,
          <a href={mapsUrl} style={linkStyle} {...external} {...hover}>
            {t('publicLanding.footer.map')}
          </a>,
        )}
      {socials.length > 0 && (
        <div
          style={{ display: 'flex', gap: 10, marginTop: 4 }}
          role="list"
          aria-label={t('publicLanding.footer.social')}
        >
          {socials.map((s) => (
            <a
              key={s.key}
              role="listitem"
              href={s.url!}
              aria-label={`${t(`publicLanding.footer.${s.key}`)} ${t('publicLanding.footer.newTab')}`}
              {...external}
              style={{
                width: 34,
                height: 34,
                borderRadius: '50%',
                border: `1px solid ${BORDER}`,
                display: 'grid',
                placeItems: 'center',
                color: TEXT_SECONDARY,
              }}
              {...hover}
            >
              {s.icon}
            </a>
          ))}
        </div>
      )}
    </div>
  );
}
